export interface UploadedReferenceImage {
  contentType?: string;
  id: string;
  originalName?: string;
  size?: number;
  url: string;
}

export interface ReferenceImageUploadResult {
  files: UploadedReferenceImage[];
  ids: string[];
}

// Image models downscale references internally, so camera originals (e.g.
// 6000 px, 8+ MB) add upload and moderation latency without adding detail.
export const REFERENCE_IMAGE_MAX_EDGE = 3072;
export const REFERENCE_IMAGE_MAX_BYTES = 4 * 1024 * 1024;
const REFERENCE_IMAGE_JPEG_QUALITY = 0.9;

const RESIZABLE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

interface ReferenceImageResizePlan {
  contentType: 'image/jpeg' | 'image/webp';
  height: number;
  width: number;
}

/** Returns the target encoding for an oversized image, or null to keep it. */
export function getReferenceImageResizePlan({
  contentType,
  height,
  size,
  width,
}: {
  contentType: string;
  height: number;
  size: number;
  width: number;
}): ReferenceImageResizePlan | null {
  if (!RESIZABLE_TYPES.includes(contentType)) return null;
  if (width <= 0 || height <= 0) return null;

  const longestEdge = Math.max(width, height);
  if (
    longestEdge <= REFERENCE_IMAGE_MAX_EDGE &&
    size <= REFERENCE_IMAGE_MAX_BYTES
  ) {
    return null;
  }

  const scale = Math.min(1, REFERENCE_IMAGE_MAX_EDGE / longestEdge);
  return {
    // PNG/WebP may carry transparency, which JPEG would flatten.
    contentType: contentType === 'image/jpeg' ? 'image/jpeg' : 'image/webp',
    height: Math.max(1, Math.round(height * scale)),
    width: Math.max(1, Math.round(width * scale)),
  };
}

function replaceExtension(name: string, contentType: string) {
  const extension =
    contentType === 'image/webp'
      ? 'webp'
      : contentType === 'image/png'
        ? 'png'
        : 'jpg';
  const base = name.replace(/\.[^./\\]+$/, '') || 'reference';
  return `${base}.${extension}`;
}

/**
 * Downscales oversized reference images in the browser before upload. Any
 * decode/encode failure falls back to the original file so the server still
 * applies its own validation.
 */
async function prepareReferenceImageForUpload(file: File) {
  if (!RESIZABLE_TYPES.includes(file.type)) return file;
  if (typeof createImageBitmap !== 'function') return file;

  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(file);
    const plan = getReferenceImageResizePlan({
      contentType: file.type,
      height: bitmap.height,
      size: file.size,
      width: bitmap.width,
    });
    if (!plan) return file;

    const canvas = document.createElement('canvas');
    canvas.width = plan.width;
    canvas.height = plan.height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, plan.width, plan.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, plan.contentType, REFERENCE_IMAGE_JPEG_QUALITY)
    );
    // Browsers without WebP encoding fall back to PNG, which can be larger.
    if (!blob || !RESIZABLE_TYPES.includes(blob.type)) return file;
    // A size-only re-encode is pointless if it did not shrink the file.
    if (plan.width === bitmap.width && blob.size >= file.size) return file;

    return new File([blob], replaceExtension(file.name, blob.type), {
      lastModified: file.lastModified,
      type: blob.type,
    });
  } catch {
    return file;
  } finally {
    bitmap?.close();
  }
}

export async function uploadReferenceImages(
  files: File[]
): Promise<ReferenceImageUploadResult> {
  if (files.length === 0) return { files: [], ids: [] };

  const form = new FormData();
  // Sequential so several camera originals are never decoded at once.
  for (const file of files) {
    form.append('files', await prepareReferenceImageForUpload(file));
  }

  const response = await fetch('/api/generate/reference-images', {
    method: 'POST',
    body: form,
  });

  const payload = (await response.json().catch(() => null)) as {
    error?: string;
    files?: UploadedReferenceImage[];
    ids?: string[];
  } | null;

  if (!response.ok) {
    throw new Error(payload?.error ?? 'Reference image upload failed.');
  }

  const filesPayload = payload?.files ?? [];
  const ids = payload?.ids ?? filesPayload.map((file) => file.id);
  return { files: filesPayload, ids };
}
